

import React, { useState, useEffect } from "react";
import "./missionvissioncards.css";
import { useLoader } from "../../../../context/LoaderProvider";
import { aboutUsService } from "../../../../Service/aboutUsService";

// Default placeholder images
import missionPlaceholder from "../../../../images/aaaservices/mision.png";
import visionPlaceholder from "../../../../images/aaaservices/vision-logo.png";

const MissionVision = () => {
  const [loading, setLoading] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const [aboutId, setAboutId] = useState(null);
  const [charLimit, setCharLimit] = useState(120);
  const [data, setData] = useState({
    mission: "",
    vision: "",
    missionImage: missionPlaceholder,
    visionImage: visionPlaceholder
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    text: "",
    imageFile: null,
    imagePreview: null
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success"
  });

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ open: true, message, type });
    setTimeout(() => {
      setSnackbar(prev => ({ ...prev, open: false }));
    }, 3000);
  };

  // Helper function to get full image URL
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    if (imagePath.startsWith("food/")) {
      return `https://backend.maamaas.com/${imagePath}`;
    }
    return `https://backend.maamaas.com/food/${imagePath}`;
  };

  // ======================
  // GET Mission Vision
  // ======================
  const fetchData = async () => {
    try {
      showLoader();
      const result = await aboutUsService.getMissionVision();

      // ✅ Debug log to see what the API returns

      // ✅ Extract aboutUsId from response
      const id = result?.aboutUsId || result?.id || null;

      if (id) {
        setAboutId(id);
      } else {
        console.warn("⚠️ No aboutUsId found in response");
      }

      setData({
        mission: result.mission || "",
        vision: result.vision || "",
        missionImage: result.missionImage ? getFullImageUrl(result.missionImage) : missionPlaceholder,
        visionImage: result.visionImage ? getFullImageUrl(result.visionImage) : visionPlaceholder
      });
    } catch (error) {
      console.error("Error fetching mission/vision:", error);
      if (!error.message?.includes("404")) {
        showSnackbar("Failed to fetch mission/vision", "error");
      }
    } finally {
      hideLoader();
    }
  };
  // Add this function before saveInlineEdit
  const ensureAboutUsId = async () => {
    // If we already have aboutId, return it
    if (aboutId) {
      return aboutId;
    }

    // If not, fetch fresh data
    try {
      const result = await aboutUsService.getMissionVision();
      const id = result?.aboutUsId || result?.id || null;

      if (id) {
        setAboutId(id);
        return id;
      } else {
        throw new Error("No about us record found");
      }
    } catch (error) {
      console.error("❌ Failed to fetch aboutUsId:", error);
      throw new Error("Please create About Us content first");
    }
  };
  useEffect(() => {
    const savedLimit = localStorage.getItem('missionVisionCharLimit');
    if (savedLimit) {
      setCharLimit(parseInt(savedLimit));
    }
    fetchData();
  }, []);

  // ======================
  // OPEN EDIT
  // ======================
  const openInlineEdit = (type) => {
    setEditTarget(type);
    setEditForm({
      text: type === "mission" ? data.mission : data.vision,
      imageFile: null,
      imagePreview: type === "mission" ? data.missionImage : data.visionImage
    });
    setEditModalOpen(true);
  };

  // ======================
  // TEXT CHANGE with character limit
  // ======================
  const handleEditTextChange = (e) => {
    const text = e.target.value;
    if (text.length <= charLimit) {
      setEditForm({
        ...editForm,
        text: text
      });
    }
  };

  // ======================
  // IMAGE CHANGE
  // ======================
  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showSnackbar("Please select a valid image file", "warning");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar("Image size should be less than 5MB", "warning");
        return;
      }

      setEditForm({
        ...editForm,
        imageFile: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  // ======================
  // UPDATE API
  // ======================
  const saveInlineEdit = async () => {
    if (!editForm.text.trim()) {
      showSnackbar("Please enter description", "warning");
      return;
    }

    if (editForm.text.length > charLimit) {
      showSnackbar(`Description exceeds ${charLimit} characters limit`, "warning");
      return;
    }

    setLoading(true);
    try {
      showLoader();

      // ✅ Ensure we have aboutUsId before updating
      const currentAboutId = await ensureAboutUsId();

      if (!currentAboutId) {
        throw new Error("No about us record found. Please create About Us content first.");
      }


      let mission = null;
      let vision = null;
      let missionImage = null;
      let visionImage = null;

      if (editTarget === "mission") {
        mission = editForm.text;
        missionImage = editForm.imageFile;
      } else if (editTarget === "vision") {
        vision = editForm.text;
        visionImage = editForm.imageFile;
      }

      await aboutUsService.updateMissionVision(
        currentAboutId,
        mission,
        vision,
        missionImage,
        visionImage
      );

      showSnackbar("Updated successfully");
      setEditModalOpen(false);
      await fetchData(); // Refresh data after update
    } catch (error) {
      console.error("Update error:", error);
      showSnackbar(error.message || "Update failed", "error");
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // ======================
  // DELETE / RESET
  // ======================
  const deleteCard = async (type) => {
    if (!window.confirm(`Are you sure you want to reset ${type === "mission" ? "Mission" : "Vision"}?`)) return;

    try {
      showLoader();

      // ✅ Ensure we have aboutUsId
      const currentAboutId = await ensureAboutUsId();

      if (!currentAboutId) {
        throw new Error("No about us record found");
      }

      let mission = type === "mission" ? "" : data.mission;
      let vision = type === "vision" ? "" : data.vision;
      let missionImage = null;
      let visionImage = null;

      await aboutUsService.updateMissionVision(
        currentAboutId,
        mission,
        vision,
        missionImage,
        visionImage
      );

      showSnackbar(`${type === "mission" ? "Mission" : "Vision"} reset successfully`, "success");
      await fetchData();
    } catch (error) {
      console.error("Reset error:", error);
      showSnackbar("Reset failed", "error");
    } finally {
      hideLoader();
    }
  };

  // Handle character limit change
  const handleCharLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setCharLimit(newLimit);
    localStorage.setItem('missionVisionCharLimit', newLimit);
  };

  const remainingChars = charLimit - editForm.text.length;
  const isNearLimit = remainingChars <= 50;
  const isOverLimit = editForm.text.length > charLimit;

  return (
    <section className="mv-section">
      <div className="mv-container">
        {/* Cards Grid */}
        <div className="mv-grid">
          {/* Mission Card */}
          <div className="mv-card">
            <div className="mv-card-image-wrapper">
              <img
                src={data.missionImage}
                alt="Our Mission"
                className="mv-card-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = missionPlaceholder;
                }}
              />
              <div className="mv-card-buttons">
                <button
                  className="mv-edit-btn"
                  onClick={() => openInlineEdit("mission")}
                  title="Edit Mission"
                >
                  <svg className="mv-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>

                </button>
                {/* <button
                  className="mv-delete-btn"
                  onClick={() => deleteCard("mission")}
                  title="Reset Mission"
                >
                  <svg className="mv-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button> */}
              </div>
            </div>
            <div className="mv-card-content">
              <h3 className="mv-card-title">Our Mission</h3>
              <p className="mv-card-description">
                {data.mission || "Click edit to add mission description"}
              </p>
            </div>
          </div>

          {/* Vision Card */}
          <div className="mv-card">
            <div className="mv-card-image-wrapper">
              <img
                src={data.visionImage}
                alt="Our Vision"
                className="mv-card-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = visionPlaceholder;
                }}
              />
              <div className="mv-card-buttons">
                <button
                  className="mv-edit-btn"
                  onClick={() => openInlineEdit("vision")}
                  title="Edit Vision"
                >
                  <svg className="mv-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>

                </button>
                {/* <button
                  className="mv-delete-btn"
                  onClick={() => deleteCard("vision")}
                  title="Reset Vision"
                >
                  <svg className="mv-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button> */}
              </div>
            </div>
            <div className="mv-card-content">
              <h3 className="mv-card-title">Our Vision</h3>
              <p className="mv-card-description">
                {data.vision || "Click edit to add vision description"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal - Consistent with About Us */}
      {editModalOpen && (
        <div className="mv-modal-overlay">
          <div className="mv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mv-modal-header">
              <h3>Edit {editTarget === "mission" ? "Mission" : "Vision"}</h3>
              <button
                className="mv-modal-close"
                onClick={() => setEditModalOpen(false)}
                disabled={loading}
              >
                ×
              </button>
            </div>

            <div className="mv-modal-body">

              {/* Image Upload Section */}
              <div className="mv-modal-image-section">
                {/* <label className="mv-modal-label">
                  Image
                </label> */}
                <div className="mv-modal-image-container">
                  {editForm.imagePreview ? (
                    <div className="mv-modal-image-preview-wrapper">
                      <img src={editForm.imagePreview} alt="Preview" className="mv-modal-image-preview" />
                      <button
                        className="mv-modal-image-remove"
                        onClick={() => {
                          setEditForm({
                            ...editForm,
                            imageFile: null,
                            imagePreview: editTarget === "mission" ? data.missionImage : data.visionImage
                          });
                        }}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="mv-modal-image-placeholder">
                      <svg className="mv-placeholder-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>No image selected</span>
                    </div>
                  )}
                  <button className="mv-modal-upload-btn">
                    <svg className="mv-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {editForm.imagePreview && editForm.imagePreview !== (editTarget === "mission" ? data.missionImage : data.visionImage) ? 'Change Image' : 'Upload Image'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditImageChange}
                      className="mv-file-input"
                      disabled={loading}
                    />
                  </button>
                </div>
              </div>
              {/* Character Limit Settings */}
              {/* <div className="mv-modal-limit-section">
                <label className="mv-modal-label">
                  Character Limit
                  <span className="mv-modal-label-hint">(Set maximum characters allowed)</span>
                </label>
                <select 
                  className="mv-modal-limit-select"
                  value={charLimit}
                  onChange={handleCharLimitChange}
                  disabled={loading}
                >
                  <option value="300">300 characters</option>
                  <option value="500">500 characters</option>
                  <option value="1000">1000 characters</option>
                  <option value="1500">1500 characters</option>
                  <option value="2000">2000 characters</option>
                </select>
              </div> */}

              {/* Text Edit Section */}
              <div className="mv-modal-text-section">
                <label className="mv-modal-label">
                  Description
                  {/* <span className="mv-modal-label-hint">(Tell your story)</span> */}
                </label>
                <textarea
                  className={`mv-modal-textarea ${isOverLimit ? 'mv-modal-textarea-error' : ''}`}
                  value={editForm.text}
                  onChange={handleEditTextChange}
                  placeholder={`Enter ${editTarget === "mission" ? "mission" : "vision"} description...`}
                  rows="6"
                  disabled={loading}
                />

                {/* Character Counter */}
                <div className={`mv-modal-char-counter ${isNearLimit ? 'near-limit' : ''} ${isOverLimit ? 'over-limit' : ''}`}>
                  <span className="mv-modal-char-count">
                    {editForm.text.length} / {charLimit} characters
                  </span>

                </div>
              </div>


            </div>

            <div className="mv-modal-footer">
              <button
                className="mv-modal-cancel-btn"
                onClick={() => setEditModalOpen(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="mv-modal-save-btn"
                onClick={saveInlineEdit}
                disabled={loading || isOverLimit || !editForm.text.trim()}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar Notification */}
      {snackbar.open && (
        <div className={`mv-snackbar mv-snackbar-${snackbar.type}`}>
          {snackbar.message}
        </div>
      )}
    </section>
  );
};

export default MissionVision;